(() => {
  /**
   * Custom Slider Component
   * Universal Flickity slider with configurable options via JSON
   *
   * Usage:
   * <custom-slider>
   *   <div data-slider>
   *     <div data-slider-item>...</div>
   *     <div data-slider-item>...</div>
   *   </div>
   *   <script type="application/json">
   *     { "autoPlay": 3000, "wrapAround": true }
   *   </script>
   * </custom-slider>
   */
  
  class CustomSlider extends HTMLElement {
    constructor() {
      super();
      this.sliderElement = null;
      this.flickity = null;
      this.sliderId = null;
      this.handleResize = this.handleResize.bind(this);
      this.updateAutoplayVisibility = this.updateAutoplayVisibility.bind(this);
    }
  
    connectedCallback() {
      this.style.display = 'block';
  
      const configScript = this.querySelector('script[type="application/json"]');
      if (!configScript) {
        console.error('Custom Slider: Configuration script not found');
        return;
      }
  
      let config;
      try {
        config = JSON.parse(configScript.textContent);
      } catch (error) {
        console.error('Custom Slider: Invalid JSON configuration', error);
        return;
      }
  
      this.sliderElement = this.querySelector('[data-slider]');
      if (!this.sliderElement) {
        console.error('Custom Slider: Element with [data-slider] attribute not found');
        return;
      }
  
      this.sliderId = this.sliderElement.getAttribute('data-slider-id') || 'unknown';
  
      this.initializeSlider(config);
    }
  
    initializeSlider(config) {
      if (typeof Flickity === 'undefined') {
        console.error('Custom Slider: Flickity library not loaded');
        return;
      }
  
      const defaultConfig = {
        cellAlign: 'left',
        contain: true,
        wrapAround: false,
        autoPlay: false,
        prevNextButtons: true,
        pageDots: true,
        draggable: true,
      };
  
      const flickityConfig = { ...defaultConfig, ...config };
  
      this.flickity = new Flickity(this.sliderElement, flickityConfig);

      if (flickityConfig.autoPlay && this.hasAttribute('data-pause-offscreen') && 'IntersectionObserver' in window) {
        this.autoplayInView = false;
        this.autoplayStoppedByUser = false;
        const stopForUser = () => { this.autoplayStoppedByUser = true; };
        // Preserve Flickity's intentional stop after a swipe or navigation.
        this.flickity.on('pointerDown', stopForUser);
        this.flickity.on('uiChange', stopForUser);
        this.updateAutoplayVisibility();
        this.autoplayObserver = new IntersectionObserver(entries => {
          this.autoplayInView = entries[0].isIntersecting;
          this.updateAutoplayVisibility();
        });
        this.autoplayObserver.observe(this);
        document.addEventListener('visibilitychange', this.updateAutoplayVisibility);
        this.sliderElement.addEventListener('mouseleave', this.updateAutoplayVisibility);
      }
  
      window.addEventListener('resize', this.handleResize);
    }

    updateAutoplayVisibility() {
      if (!this.flickity) return;
      if (!this.autoplayInView || document.hidden || this.autoplayStoppedByUser) {
        this.flickity.stopPlayer();
      } else if (!this.flickity.options.pauseAutoPlayOnHover || !this.sliderElement.matches(':hover')) {
        this.flickity.playPlayer();
      }
    }
  
    handleResize() {
      if (this.flickity) {
        this.flickity.resize();
      }
    }
  
    disconnectedCallback() {
      this.autoplayObserver?.disconnect();
      this.autoplayObserver = null;
      document.removeEventListener('visibilitychange', this.updateAutoplayVisibility);
      this.sliderElement?.removeEventListener('mouseleave', this.updateAutoplayVisibility);
      if (this.flickity) {
        this.flickity.destroy();
        this.flickity = null;
      }
      window.removeEventListener('resize', this.handleResize);
    }
  }
  
  if (!customElements.get('custom-slider')) {
    customElements.define('custom-slider', CustomSlider);
  }
})();
